import { useMemo, type CSSProperties } from 'react';
import { useLazyInView } from '../hooks/useLazyInView';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  usePropertyField,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getAccessibleTextOnBackground, getContrastTextColor } from '../utils/luminance';

// ─── Slot group definitions (inline literals — required by the schema linter) ──

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Fixed wave-pattern background ─────────────────────────────────────────────
//
// The same parallel-wave motif as <WaveBand> / the hero band, rendered as an
// SVG data-URI so it can tile as a CSS background with `background-attachment:
// fixed` (the texture stays put while the section scrolls over it). The stroke
// color is the resolved secondary token, so it stays on-brand across themes.
//
// Geometry is kept IDENTICAL to the hero band (<WaveBand> / Hero.tsx) so every
// wave motif on the page reads as the exact same pattern — spacing 15, control
// offset 25 (peak deviation 12.5), wavelength 280 (two 140px humps per tile),
// 1.5px stroke. This matches the tightened interior-page hero waves. Only the
// styling differs (this one tiles as a fixed CSS background instead of a one-off
// band).
//
// The tile still repeats SEAMLESSLY in both directions:
//   • Horizontal — the `q70 … t140 0` path returns to its baseline (and slope)
//     at x = WAVELENGTH, so the next tile continues without a step.
//   • Vertical — TILE_H is an exact multiple of LINE_SPACING. Because the deep
//     crests rise AMP/2 = 12.5px (> LINE_SPACING/2 = 7.5), a wave DOES cross the
//     tile's top/bottom edge, so we draw one extra row (i = 0…LINE_COUNT). The
//     boundary rows at y=0 and y=TILE_H are the same curve one period apart, so
//     a crest clipped at one edge is completed by the adjacent tile; the doubled
//     boundary row is an opaque stroke, so it redraws identically with no
//     visible artifact. (Peak deviation 12.5 stays below LINE_SPACING 15, so
//     only the single boundary row clips — one extra row is enough.)
const WAVELENGTH = 280; // horizontal period (two humps per tile)
const LINE_SPACING = 15; // vertical distance between wave baselines (matches hero)
const AMP = 25; // quadratic control offset; peak deviation = AMP/2 = 12.5 (matches hero)
const LINE_COUNT = 8;
const TILE_W = WAVELENGTH;

// Build one seamlessly-tiling wave texture. `spacing` is the vertical distance
// between wave baselines; the tile height scales with it (LINE_COUNT rows) so
// the texture always repeats cleanly. Enough extra rows are drawn above and
// below the tile (`extra`, derived from the crest depth AMP/2) that every crest
// clipped at a tile edge is completed by the adjacent tile — this stays correct
// even when the crest depth exceeds `spacing` (e.g. the tighter mobile tile).
// The returned `tileH` is the CSS backgroundSize height, NOT the graphic
// height: the section still sizes to its content, so shrinking the spacing only
// densifies the texture without changing how tall the graphic renders.
function buildWaveDataUri(color: string, spacing: number = LINE_SPACING): {
  uri: string;
  tileH: number;
} {
  const tileH = spacing * LINE_COUNT;
  const extra = Math.ceil(AMP / 2 / spacing);
  const lines: string[] = [];
  for (let i = -extra; i <= LINE_COUNT + extra; i++) {
    const y = i * spacing;
    lines.push(
      `<path d='M0 ${y} q70 ${-AMP} 140 0 t140 0' fill='none' stroke='${color}' stroke-width='1.5'/>`,
    );
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE_W}' height='${tileH}'>${lines.join('')}</svg>`;
  return { uri: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`, tileH };
}

// ─── Google Maps embed ─────────────────────────────────────────────────────────

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  const tokens = useTokens();
  const secondaryColor = tokens?.color?.secondary || '#B28C59';
  // Desktop (≥ lg) keeps the standard spacing; mobile + tablet (< lg) halve it
  // so the waves pack twice as densely (the graphic's height is unchanged).
  const waveBgDesktop = useMemo(() => buildWaveDataUri(secondaryColor, LINE_SPACING), [secondaryColor]);
  const waveBgMobile = useMemo(() => buildWaveDataUri(secondaryColor, LINE_SPACING / 2), [secondaryColor]);

  // Prefer primary on the surface (muting overlay); fall back when contrast is too low.
  const headingColor = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.surface, tokens?.color?.primary),
    [tokens],
  );

  // The body copy sits over the `~88% surface` muting overlay, so it effectively
  // reads against `--vibe-color-surface`. The default `--vibe-color-text` is
  // paired with the page background, so a dark `surface` (property_color_3)
  // leaves the copy dark-on-dark. Keep the themed text when it clears WCAG AA
  // against surface; otherwise fall back to a luminance-picked black/white.
  // Mirrors the banner-text fix in AmenitiesFeatures.
  const bodyTextStyle = useMemo<CSSProperties | undefined>(() => {
    const surface = tokens?.color?.surface;
    if (!surface) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(surface, themed) >= 4.5 ? themed : getContrastTextColor(surface);
    return { ['--pc-body-text' as string]: color } as CSSProperties;
  }, [tokens]);

  const cta = useSlotGroup('pinnedcallouts.cta', ctaDef);
  const [mapRef, mapVisible] = useLazyInView<HTMLDivElement>();

  // Property-centred map — mirrors ContactMap's embed logic.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!lat && !!lng && lat.trim() !== '' && lng.trim() !== '';

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=14`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  return (
    <section className="relative w-full overflow-hidden">
      {/* ── Fixed wave texture (z-0) ── */}
      {/* Mobile + tablet (< lg): half spacing → denser waves; scrolls with the section. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-scroll lg:hidden"
        style={{
          backgroundImage: waveBgMobile.uri,
          backgroundRepeat: 'repeat',
          backgroundSize: `${TILE_W}px ${waveBgMobile.tileH}px`,
        }}
      />
      {/* Desktop (≥ lg): standard spacing; texture stays put while the section scrolls. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 bg-fixed hidden lg:block"
        style={{
          backgroundImage: waveBgDesktop.uri,
          backgroundRepeat: 'repeat',
          backgroundSize: `${TILE_W}px ${waveBgDesktop.tileH}px`,
        }}
      />
      {/* ── Muting overlay (z-[1]) — keeps the copy readable over the texture ── */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] bg-[color-mix(in_srgb,var(--vibe-color-surface)_88%,transparent)]"
      />

      {/* ── Content (z-10) ── */}
      <div
        ref={revealRef}
        style={bodyTextStyle}
        className="reveal-fade-up relative z-10 grid grid-cols-1 md:grid-cols-2 items-stretch gap-10 md:gap-0 py-[50px] md:py-[70px] lg:py-[50px]"
      >
        {/* Map column (full bleed to the left/top/bottom edges).
            On mobile it drops below the copy (order-2); desktop keeps it left (order-1). */}
        <div
          ref={mapRef}
          className="order-2 md:order-1 relative w-full h-full overflow-hidden min-h-[360px] md:min-h-[520px] bg-[var(--vibe-color-surface)]"
        >
          {mapSrc && mapVisible ? (
            <iframe
              className="absolute inset-0 w-full h-full border-0"
              src={mapSrc}
              title="Property location map"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : mapSrc ? null : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
                Map location not available
              </p>
            </div>
          )}
        </div>

        {/* Text column — heading/copy/CTA. Sits above the map on mobile (order-1),
            right column on desktop (order-2). */}
        <div className="order-1 md:order-2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="w-full md:max-w-2xl md:mr-auto md:pl-4 lg:pl-8">
            <RichTextContent
              path="pinnedcallouts.heading"
              label="Heading"
              defaultValue="<p>Find Your Island Escape</p>"
              as="h2"
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] uppercase mb-4 [&_a]:underline"
              style={{ color: headingColor }}
            />

            <RichTextContent
              path="pinnedcallouts.body"
              label="Body Text"
              defaultValue="<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>"
              as="div"
              className="font-body text-base text-[color:var(--pc-body-text,var(--vibe-color-text))] leading-relaxed [&_a]:underline mb-6"
            />

            <EditableSlotGroup groupPath="pinnedcallouts.cta" definition={ctaDef}>
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="pc-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {cta.label || 'Explore Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Richtext links inherit the heading/body color instead of the
           global --vibe-color-hyperlink token. */
        [data-vibesite-slot="PinnedCallouts"] [data-vibesite-richtext] a,
        [data-vibesite-slot="PinnedCallouts"] [data-vibesite-richtext] a:hover,
        [data-vibesite-slot="PinnedCallouts"] [data-vibesite-richtext] a:visited {
          color: inherit !important;
        }
        /* Solid pill CTA — filled with the theme primary, matching the
           reference stripe's "Explore Neighborhood" button. */
        .pc-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .pc-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Island Escape</p>',
    },
    'pinnedcallouts.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        "<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>",
    },
  },
  slotGroups: {
    'pinnedcallouts.cta': ctaDef,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
